import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Alert, Empty, Input, Spin } from 'antd';
import {
  usePassport,
  usePassportArrivals,
  usePassportTransactions,
} from '@/api/Passport';
import { useRepresentativeImage } from '@/api/ProductImages/useRepresentativeImage';
import { PassportsTransactionData } from '@/types';
import { timeConverter } from '@/utils/helpers';
import { GbaLogo } from '@/ui/components/Logo/GbaLogo';
import { ArrivalStamp } from './ArrivalStamp';
import { TransactionLedger } from './TransactionLedger';
import { LocationEvidence } from './LocationEvidence';
import './PassportBook.scss';

type View = 'identity' | 'visits' | 'transactions';
// 'all', 'unconfirmed' or a recorded site name.
type VisitFilter = string;

const VIEWS: [View, string][] = [
  ['identity', '01 Identity'],
  ['visits', '02 Site visits'],
  ['transactions', '03 All transactions'],
];

const day = (value?: string | null) =>
  timeConverter(value || undefined, 'DD MMM YYYY');
const dayTime = (value?: string | null) =>
  timeConverter(value || undefined, 'DD MMM YYYY HH:mm');

const Field: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div className="pb-field">
    <span className="pb-label">{label}</span>
    <span className="pb-value">{children || '—'}</span>
  </div>
);

export const PassportBook: FC<{ vin: string }> = ({ vin }) => {
  const enabled = !!vin;
  const passportQuery = usePassport(vin, { enabled });
  const transactionsQuery = usePassportTransactions(vin, { enabled });
  const arrivalsQuery = usePassportArrivals(vin, { enabled });
  const passport = passportQuery.data;

  const { image } = useRepresentativeImage(
    passport?.vehicleId,
    !passport?.images?.length,
  );

  const [view, setView] = useState<View>('identity');
  const [selectedId, setSelectedId] = useState<number>();
  const [search, setSearch] = useState('');
  const [visitFilter, setVisitFilter] = useState<VisitFilter>('all');
  const [evidence, setEvidence] = useState<PassportsTransactionData>();

  useEffect(
    () => setEvidence(undefined),
    [view, visitFilter, search, selectedId],
  );

  const arrivals = useMemo(
    () =>
      [...(arrivalsQuery.data || [])].sort((a, b) =>
        a.arrivedAt.localeCompare(b.arrivedAt),
      ),
    [arrivalsQuery.data],
  );
  const transactions = useMemo(
    () => transactionsQuery.data || [],
    [transactionsQuery.data],
  );
  // Visit filtering needs an explicit association from the API; never infer it.
  const hasVisitLink = transactions.some((t) => t.arrivalId != null);
  // Arrival sites in visit order, then any other site a transaction names.
  const sites = useMemo(
    () => [
      ...new Set(
        [
          ...arrivals.map((a) => a.siteName),
          ...transactions.map((t) => t.locationDesc?.trim()),
        ].filter(Boolean),
      ),
    ],
    [arrivals, transactions],
  );
  const hasUnconfirmed = transactions.some((t) => !t.locationDesc?.trim());
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return transactions.filter(
      (t) =>
        (visitFilter === 'all' ||
          (visitFilter === 'unconfirmed'
            ? !t.locationDesc?.trim()
            : t.locationDesc?.trim() === visitFilter)) &&
        (!term ||
          [t.transDescription, t.transType, t.trackingNumber, t.locationDesc]
            .join(' ')
            .toLowerCase()
            .includes(term)),
    );
  }, [transactions, search, visitFilter]);

  if (!enabled)
    return (
      <Empty className="mt-5" description="Search a VIN to open its passport" />
    );
  if (passportQuery.isLoading) return <Spin className="d-block mt-5" />;
  if (passportQuery.isError)
    return (
      <Alert
        className="mt-4"
        type="error"
        showIcon
        message="The passport could not be loaded. Please try again."
      />
    );
  if (!passport?.vehicleId)
    return <Empty className="mt-5" description="No vehicle matches this VIN" />;

  const selected =
    arrivals.find((a) => a.arrivalId === selectedId) ?? arrivals[0];
  const last = arrivals[arrivals.length - 1];
  // Without an API visit link, a visit runs from its arrival to its departure,
  // or to the next recorded arrival when no departure was recorded.
  const visitEnd =
    selected &&
    (selected.departedAt ??
      arrivals[arrivals.indexOf(selected) + 1]?.arrivedAt);
  const visitTransactions = selected
    ? transactions.filter((t) => {
        if (hasVisitLink) return t.arrivalId === selected.arrivalId;
        const at = new Date(t.transDate).getTime();
        return (
          at >= new Date(selected.arrivedAt).getTime() &&
          (!visitEnd || at <= new Date(visitEnd).getTime())
        );
      })
    : [];
  const photo = passport.images?.[0];

  const arrivalsPage = (
    <>
      <h2 className="pb-heading">Recorded arrivals</h2>
      <p className="pb-sub">
        Each stamp is a separate site visit. Select one for its details.
      </p>
      {arrivalsQuery.isLoading ? (
        <Spin />
      ) : arrivalsQuery.isError ? (
        <p className="pb-note">
          Arrival history is unavailable. The full transaction ledger remains
          available.
        </p>
      ) : !arrivals.length ? (
        <p className="pb-note">
          No arrivals have been recorded for this vehicle.
        </p>
      ) : (
        <div className="pb-stamps">
          {arrivals.map((arrival, index) => (
            <ArrivalStamp
              key={arrival.arrivalId}
              arrival={arrival}
              index={index}
              selected={arrival === selected}
              onSelect={() => setSelectedId(arrival.arrivalId)}
            />
          ))}
          {view === 'identity' && selected && (
            <div className="pb-selected">
              <span className="pb-label">Selected stamp</span>
              <strong>{selected.siteName}</strong>
              <small>Arrived {day(selected.arrivedAt)}</small>
              <button
                type="button"
                className="pb-link"
                onClick={() => setView('visits')}
              >
                Open visit &amp; transactions →
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );

  const leftPage: Record<View, ReactNode> = {
    identity: (
      <>
        <h2 className="pb-heading">Vehicle identity</h2>
        <div className="pb-identity">
          <figure className="pb-photo">
            <img
              src={
                photo?.resizedImageUrl ||
                photo?.url ||
                image?.url ||
                '/images/reference-vehicle.jpg'
              }
              alt={`${passport.manufacturer} ${passport.model}`}
            />
            <figcaption>
              {photo ? 'Vehicle photo' : 'Reference vehicle'}
            </figcaption>
          </figure>
          <div>
            <Field label="Make / model">
              {[passport.manufacturer, passport.model]
                .filter(Boolean)
                .join(' ')}
            </Field>
            <Field label="VIN">{passport.vin}</Field>
            <Field label="Registration">{passport.regNumber}</Field>
            <Field label="Colour">{passport.colour}</Field>
            <Field label="Status at last record">
              {[passport.vinStatus, passport.vinStatusDesc]
                .filter(Boolean)
                .join(' - ')}
            </Field>
          </div>
        </div>
        <Field label="Last recorded arrival">
          {last
            ? `${last.siteName} · ${day(last.arrivedAt)}`
            : arrivalsQuery.isError
              ? 'Arrival history unavailable'
              : 'No recorded arrival'}
        </Field>
        {/* Hidden for now; restore when the historical-record note is wanted again.
        <div className="pb-note">
          <span className="pb-label">Historical record</span>
          Present whereabouts are not established by OneView.
        </div>
        */}
      </>
    ),
    visits: selected ? (
      <>
        <h2 className="pb-heading pb-heading-lg">
          {`Visit ${String(selected.visitNumber).padStart(2, '0')} / ${selected.siteName}`}
        </h2>
        <div className="pb-visit-times">
          <div>
            <span className="pb-label">Arrival recorded</span>
            <strong>{dayTime(selected.arrivedAt)}</strong>
          </div>
          <div>
            <span className="pb-label">Departure recorded</span>
            <strong>
              {selected.departedAt
                ? dayTime(selected.departedAt)
                : 'Departure not recorded'}
            </strong>
          </div>
        </div>
        <span className="pb-label mt-4 mb-1 d-block">Visit summary</span>
        <dl className="pb-summary">
          <dt>Customer / site</dt>
          <dd>{selected.siteName}</dd>
          <dt>Recorded arrivals</dt>
          <dd>
            {arrivals.filter((a) => a.siteCode === selected.siteCode).length}
          </dd>
          <dt>Transactions in visit</dt>
          <dd>{visitTransactions.length}</dd>
          <dt>Visit classification</dt>
          <dd>
            {selected.departedAt
              ? 'Historical - departed'
              : 'Departure not recorded'}
          </dd>
        </dl>
        <div className="pb-note mt-4">
          <span className="pb-label">Stamp interaction</span>
          Choose another stamp to switch visits. Select All transactions to see
          the vehicle-wide audit record.
        </div>
      </>
    ) : (
      <p className="pb-note">Select a recorded arrival to see its visit.</p>
    ),
    transactions: (
      <>
        <h2 className="pb-heading">Search the full record</h2>
        <Input
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search event, transaction code or reference..."
          aria-label="Search transactions"
        />
        <span className="pb-label pb-filter-label">Filter by site visit</span>
        <div className="pb-filters">
          {[
            ['all', 'All visits'],
            ...sites.map((site) => [site, site]),
            ...(hasUnconfirmed ? [['unconfirmed', 'Site unconfirmed']] : []),
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={visitFilter === value}
              onClick={() => setVisitFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="pb-note mt-3">
          The transaction record remains complete even when its site association
          cannot be confirmed.
        </p>
      </>
    ),
  };

  const rightPage =
    view === 'visits' && selected ? (
      <TransactionLedger
        key={selected.arrivalId}
        compact
        title="Transactions during this visit"
        transactions={visitTransactions}
        total={visitTransactions.length}
        onShowPosition={setEvidence}
      />
    ) : view !== 'transactions' ? (
      arrivalsPage
    ) : transactionsQuery.isLoading ? (
      <Spin />
    ) : transactionsQuery.isError ? (
      <p className="pb-note">Transactions could not be loaded.</p>
    ) : (
      <TransactionLedger
        key={`${search}|${visitFilter}`}
        transactions={filtered}
        total={transactions.length}
        onShowPosition={setEvidence}
      />
    );

  return (
    <div
      className={`pb-book${evidence ? ' pb-book--evidence' : ''}`}
      aria-label="Vehicle passport"
    >
      <div className="pb-page pb-left">
        <div className="pb-watermark" aria-hidden="true">
          <GbaLogo fill="currentColor" />
        </div>
        <nav className="pb-nav" aria-label="Passport sections">
          {VIEWS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-current={view === key ? 'page' : undefined}
              onClick={() => setView(key)}
            >
              {label}
            </button>
          ))}
          <span className="pb-nav-icon" aria-hidden="true">
            <GbaLogo fill="currentColor" width="27" height="24" />
          </span>
        </nav>
        <div className="pb-content">{leftPage[view]}</div>
        {evidence && (
          <div
            className="pb-dim"
            aria-hidden="true"
            onClick={() => setEvidence(undefined)}
          />
        )}
      </div>
      <div className="pb-page pb-right">
        <div className="pb-watermark" aria-hidden="true">
          <GbaLogo fill="currentColor" />
        </div>
        <div className="pb-nav">
          <span className="pb-value">VIN {passport.vin}</span>
        </div>
        <div className="pb-content">
          {/* Hidden, not removed, so the ledger keeps its page and sort order. */}
          <div hidden={!!evidence} className="h-100">
            {rightPage}
          </div>
          {evidence && (
            <LocationEvidence
              transaction={evidence}
              onClose={() => setEvidence(undefined)}
            />
          )}
        </div>
        {evidence && (
          <div
            className="pb-dim"
            aria-hidden="true"
            onClick={() => setEvidence(undefined)}
          />
        )}
      </div>
    </div>
  );
};
